/**
 * API request and response shapes shared between the Hono backend and the
 * React frontend. Keep this file the single source of truth for over-the-wire
 * payloads.
 */
import { z } from 'zod';
import { TASK_STATES, ACTORS, FAILURE_REASONS, type TaskState, type Actor } from './states.js';
import { PAUSE_REASONS, type PauseReason } from './pauses.js';
import { ProfileOverridesSchema } from './profiles.js';
import { ContextInputSchema, type ContextKind, type ContextRole } from './context.js';

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export const PRIORITIES = ['1-urgent', '2-high', '3-medium', '4-low'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const TaskCreateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().default(''),
  domain: z.string().default('default'),
  priority: z.enum(PRIORITIES).default('3-medium'),
  profile_slug: z.string(),
  profile_overrides: ProfileOverridesSchema.optional(),
  owner_id: z.string(),
  assignee_id: z.string().optional(),
  reviewer_id: z.string().optional(),
  due_date: z.string().datetime().optional(),
  initial_context: z.array(ContextInputSchema).default([]),
});
export type TaskCreate = z.infer<typeof TaskCreateSchema>;

export const TaskEditSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().optional(),
  priority: z.enum(PRIORITIES).optional(),
  profile_slug: z.string().optional(),
  profile_overrides: ProfileOverridesSchema.nullable().optional(),
  assignee_id: z.string().nullable().optional(),
  reviewer_id: z.string().nullable().optional(),
  due_date: z.string().datetime().nullable().optional(),
});
export type TaskEdit = z.infer<typeof TaskEditSchema>;

export interface TaskSummary {
  id: string;
  name: string;
  description: string;
  domain: string;
  priority: Priority;
  state: TaskState;
  current_actor: Actor | null;
  pause_reason: PauseReason | null;
  failure_reason: string | null;
  profile_slug: string;
  owner_id: string;
  assignee_id: string | null;
  reviewer_id: string | null;
  due_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskDetail extends TaskSummary {
  profile_overrides: Record<string, unknown> | null;
  context: ContextRecord[];
  pending_input: PendingInputRecord | null;
  recent_events: TaskEventRecord[];
}

// ---------------------------------------------------------------------------
// Transitions and actions
// ---------------------------------------------------------------------------

export const StateTransitionSchema = z.object({
  to: z.enum(TASK_STATES),
  notes: z.string().optional(),
});
export type StateTransitionInput = z.infer<typeof StateTransitionSchema>;

export const CancelTaskSchema = z.object({
  reason: z.string().optional(),
});
export type CancelTaskInput = z.infer<typeof CancelTaskSchema>;

export const RetryTaskSchema = z.object({
  resume_from: z.enum(['triage', 'plan', 'execute']).default('execute'),
});
export type RetryTaskInput = z.infer<typeof RetryTaskSchema>;

export const ReopenTaskSchema = z.object({
  resume_from: z.enum(['triage', 'plan', 'execute']).default('execute'),
  reason: z.string().optional(),
});
export type ReopenTaskInput = z.infer<typeof ReopenTaskSchema>;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface ContextRecord {
  id: string;
  task_id: string;
  kind: ContextKind;
  role: ContextRole;
  ref: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export const EVENT_TYPES = [
  'transition',
  'pause_open',
  'pause_resolve',
  'context_added',
  'context_removed',
  'edit',
  'human_input',
  'audit_action',
  'error',
  'ai_call',
  'ai_output',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** Sub-types of `ai_output` so the UI knows what to render. */
export const AI_OUTPUT_KINDS = ['triage_classification', 'plan', 'execute_result'] as const;
export type AiOutputKind = (typeof AI_OUTPUT_KINDS)[number];

export interface TaskEventRecord {
  id: string;
  task_id: string;
  run_id: string | null;
  type: EventType;
  state: TaskState | null;
  actor: Actor;
  /** Bounded preview (up to ~4 KB), post-redaction. Used for compact list views. */
  payload_preview: string;
  /**
   * Full post-redaction payload. Present for events where the UI wants to
   * render the structured content (notably `ai_output`). Nullable because
   * tiny events skip the JSONB column.
   */
  payload: Record<string, unknown> | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Pauses
// ---------------------------------------------------------------------------

export interface PendingInputRecord {
  id: string;
  task_id: string;
  reason: PauseReason;
  payload: Record<string, unknown>;
  return_to: TaskState;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export interface AuditActionRecord {
  id: string;
  task_id: string;
  run_id: string | null;
  event_id: string | null;
  integration: string;
  tool: string;
  action_class: 'destructive' | 'external_effect';
  payload: Record<string, unknown>;
  approved_by: string | null;
  approved_at: string | null;
  executed_at: string | null;
  result: Record<string, unknown> | null;
  reversible: boolean;
  reverse_event: string | null;
  status: 'pending' | 'approved' | 'executed' | 'failed' | 'reversed' | 'dry_run';
  created_at: string;
}

// ---------------------------------------------------------------------------
// Standard error envelope
// ---------------------------------------------------------------------------

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export { TASK_STATES, ACTORS, FAILURE_REASONS, PAUSE_REASONS };
export type { TaskState, Actor, PauseReason, ContextKind, ContextRole };
